// Multiple supporting documents on an asbestos record — link an existing Document
// Library file, or upload a new one (survey / lab results / clearance / removal cert).
import { useRef, useState } from "react";
import { FileText, ExternalLink, X, Plus, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DOC_TYPE_OPTIONS } from "./constants";

export function ZoneDocuments({ zone, canEdit, documents, buildingId, clientId, onChange, onOpenDoc, onDocsRefresh }) {
    const linkedIds = zone.linked_document_ids || (zone.linked_document_id ? [zone.linked_document_id] : []);
    const linked = linkedIds.map((id) => documents.find((d) => d.id === id)).filter(Boolean);
    const available = documents.filter((d) => !linkedIds.includes(d.id));
    const [uploading, setUploading] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [docType, setDocType] = useState(DOC_TYPE_OPTIONS[0]);
    const fileRef = useRef(null);

    function removeDoc(id) {
        onChange({ linked_document_ids: linkedIds.filter((x) => x !== id) });
    }
    function addExisting(id) {
        if (!id || linkedIds.includes(id)) return;
        onChange({ linked_document_ids: [...linkedIds, id] });
    }

    async function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("client_id", clientId);
        fd.append("building_id", buildingId);
        fd.append("category", "Asbestos");
        fd.append("document_type", docType);
        fd.append("title", `${docType} — ${zone.label || zone.reference_id || "Asbestos record"}`);
        setUploading(true);
        try {
            const { data } = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange({ linked_document_ids: [...linkedIds, data.id] });
            onDocsRefresh?.();
            toast.success("Document uploaded and linked");
            setUploadOpen(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    }

    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>Documents</div>
            <div className="space-y-1.5">
                {linked.length === 0 && <div className="text-[11.5px] italic" style={{ color: "#9A968F" }}>No documents linked yet.</div>}
                {linked.map((d) => (
                    <div key={d.id} data-testid={`asbestos-zone-doc-${d.id}`} className="flex items-center gap-1.5 rounded-[7px] border px-2 py-1.5" style={{ borderColor: "#E2DDD5" }}>
                        <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: "#2A5DB0" }} />
                        <button onClick={() => onOpenDoc(d.id)} className="flex-1 min-w-0 text-left text-[11.5px] truncate hover:underline" title={d.title}>{d.title}</button>
                        <button onClick={() => onOpenDoc(d.id)} title="Open"><ExternalLink className="w-3 h-3" style={{ color: "#9A968F" }} /></button>
                        {canEdit && <button data-testid={`asbestos-zone-doc-remove-${d.id}`} onClick={() => removeDoc(d.id)} title="Unlink"><X className="w-3 h-3" style={{ color: "#B33A2E" }} /></button>}
                    </div>
                ))}
            </div>
            {canEdit && (
                <div className="mt-2 space-y-1.5">
                    {available.length > 0 && (
                        <select data-testid="asbestos-zone-doc-link-existing" value="" onChange={(e) => addExisting(e.target.value)}
                            className="w-full text-[11.5px] rounded-[7px] border px-2 py-1.5 bg-white" style={{ borderColor: "#E2DDD5" }}>
                            <option value="">+ Link an existing document…</option>
                            {available.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
                        </select>
                    )}
                    {!uploadOpen ? (
                        <button data-testid="asbestos-zone-doc-upload-toggle" onClick={() => setUploadOpen(true)}
                            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px]" style={{ color: "#C8632A", background: "#FBEDE6" }}>
                            <Plus className="w-3 h-3" /> Upload new document
                        </button>
                    ) : (
                        <div className="rounded-[8px] border p-2 space-y-1.5" style={{ borderColor: "#E2DDD5" }}>
                            <select data-testid="asbestos-zone-doc-type" value={docType} onChange={(e) => setDocType(e.target.value)}
                                className="w-full text-[11.5px] rounded-[6px] border px-2 py-1" style={{ borderColor: "#E2DDD5" }}>
                                {DOC_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <input ref={fileRef} type="file" accept="application/pdf,image/*" hidden onChange={onPickFile} data-testid="asbestos-zone-doc-file" />
                            <div className="flex items-center gap-2">
                                <button data-testid="asbestos-zone-doc-file-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
                                    className="flex-1 text-[11px] inline-flex items-center justify-center gap-1 py-1.5 rounded-[6px] text-white disabled:opacity-60" style={{ background: "#C8632A" }}>
                                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Choose file
                                </button>
                                <button onClick={() => setUploadOpen(false)} className="text-[11px] px-2 py-1.5" style={{ color: "#9A968F" }}>Cancel</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
